import React, { useMemo, useState, useRef, useEffect } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import axios from "axios";
import { toast } from 'react-toastify';

import "./HubspotContactSync.css";
import Auth, { API_URL } from "../../../../utils/auth";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Switch,
  Chip,
  Divider,
  FormHelperText,
  CircularProgress,
  Tooltip,
  ListItemText,
} from "@mui/material";

const steps = [
  "Define Active Client",
  "Map fields & choose sync direction",
  "Safe import (dry run)",
];

const DEFAULT_CLIENTBLOOM_FIELDS = [
  { key: "id", label: "ID", type: "id" },
  { key: "email", label: "Email", type: "email" },
  { key: "firstName", label: "First Name", type: "text" },
  { key: "lastName", label: "Last Name", type: "text" },
  { key: "phone", label: "Phone", type: "phone" },
  { key: "latestContactDate", label: "Latest contact date", type: "date" },
  { key: "csm", label: "CSM", type: "text" },
];

const DEFAULT_MAPPING = {
  id: { crmField: "hs_object_id", direction: "CRM→CB", readOnly: true },
  email: { crmField: "email", direction: "Two-way", readOnly: true },
  firstName: { crmField: "firstname", direction: "Two-way", readOnly: false },
  lastName: { crmField: "lastname", direction: "Two-way", readOnly: false },
  phone: { crmField: "phone", direction: "Two-way", readOnly: false },
  latestContactDate: { crmField: "notes_last_contacted", direction: "Two-way", readOnly: false },
  csm: { crmField: "hubspot_owner_id", direction: "Two-way", readOnly: false },
};

const HIDDEN_FIELDS = ['company', 'plan'];

export default function HubspotContactSync({ onClose }) {
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 state - pipeline/stage selection
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState("");
  const [selectedStages, setSelectedStages] = useState([]);
  const [confirmPaying, setConfirmPaying] = useState(false);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);

  // Step 2 state - mapping
  const [mappings, setMappings] = useState(() => {
    const defaultMappings = { ...DEFAULT_MAPPING };
    return defaultMappings;
  });

  // HubSpot data state
  const [hsContactFields, setHsContactFields] = useState([]);
  const [cbContacts, setCbContacts] = useState([]);
  const [isLoadingHsData, setIsLoadingHsData] = useState(false);
  const [hasLoadedSaved, setHasLoadedSaved] = useState(false);

  // Step 3 state - dry run / guardrails
  const [killSwitchLimit, setKillSwitchLimit] = useState(500);
  const [stopIfOverLimit, setStopIfOverLimit] = useState(true);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [syncStartTime, setSyncStartTime] = useState(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const syncIntervalRef = useRef(null);
  const [syncCompleted, setSyncCompleted] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Preview contacts
  const [previewContacts, setPreviewContacts] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const abortControllerRef = useRef(null);

  const fetchCbContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/hubspot/cb-contacts`);
      setCbContacts(response.data.clients || []);
    } catch (error) {
      console.error("Failed to fetch ClientBloom contacts:", error);
      setCbContacts([]);
    }
  };

  const fetchHubspotData = async () => {
    setIsLoadingHsData(true);
    setIsLoadingPipelines(true);

    try {
      const [pipelinesResponse, fieldsResponse] = await Promise.all([
        axios.get(`${API_URL}/hubspot/pipelines`),
        axios.get(`${API_URL}/hubspot/contact-fields`)
      ]);

      setPipelines(pipelinesResponse.data.pipelines || []);

      const fields = fieldsResponse.data.fields || [];
      setHsContactFields(fields);

      await fetchCbContacts();
    } catch (error) {
      console.error("Failed to fetch HubSpot data:", error);

      if (error.response?.status === 401 || error.response?.data?.message?.includes("token")) {
        toast.error("HubSpot connection is broken. Please reconnect your HubSpot integration.");
      } else {
        toast.error("Failed to load HubSpot data. Please check your connection.");
      }

      setPipelines([]);
      setHsContactFields([]);
    } finally {
      setIsLoadingHsData(false);
      setIsLoadingPipelines(false);
    }
  };

  useEffect(() => {
    axios.get(`${API_URL}/hubspot/field-mappings`)
    .then((response) => {
      if (response.data.mappings) {
        const savedMappings = response.data.mappings;
        const mergedMappings = { ...DEFAULT_MAPPING };
        Object.keys(savedMappings).forEach(key => {
          if (savedMappings[key] && mergedMappings[key]) {
            if (key === 'id') {
              mergedMappings.id = { crmField: "hs_object_id", direction: "CRM→CB", readOnly: true };
            } else {
              mergedMappings[key] = {
                ...mergedMappings[key],
                crmField: savedMappings[key].crmField || mergedMappings[key].crmField,
                direction: "Two-way",
                readOnly: mergedMappings[key].readOnly
              };
            }
          }
        });
        mergedMappings.id = { crmField: "hs_object_id", direction: "CRM→CB", readOnly: true };
        setMappings(mergedMappings);
      } else {
        setMappings(DEFAULT_MAPPING);
      }
    })
    .catch(() => {
      setMappings(DEFAULT_MAPPING);
    });

    fetchHubspotData();

    axios.get(`${API_URL}/hubspot/status`)
    .then((response) => {
      const raw = response?.data?.data?.config;
      let configObj = {};
      try {
        configObj = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
      } catch (e) {
        configObj = {};
      }
      const saved = configObj?.contactFilter;
      if (saved) {
        if (saved.pipelineId) setSelectedPipeline(saved.pipelineId);
        if (Array.isArray(saved.stageIds)) setSelectedStages(saved.stageIds);
        if (typeof saved.confirmPaying === 'boolean') setConfirmPaying(saved.confirmPaying);
      }
      setHasLoadedSaved(true);
    })
    .catch(() => {});
  }, []);

  // Fetch preview contacts when pipeline/stage changes
  useEffect(() => {
    if (!selectedPipeline || selectedStages.length === 0) {
      setPreviewContacts([]);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setIsLoadingPreview(true);
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const filter = { pipelineId: selectedPipeline, stageIds: selectedStages };
    const filterParam = encodeURIComponent(JSON.stringify(filter));

    axios.get(`${API_URL}/hubspot/contacts?filter=${filterParam}`, {
      timeout: 60000,
      signal: abortController.signal
    })
    .then((response) => {
      if (!abortController.signal.aborted) {
        setPreviewContacts(response.data.contacts || []);
        setIsLoadingPreview(false);
      }
    })
    .catch((error) => {
      if (axios.isCancel(error) || error.name === 'AbortError') return;
      if (error.code === 'ECONNABORTED') {
        toast.error("Request timed out. Try selecting fewer stages.");
      }
      if (!abortController.signal.aborted) {
        setPreviewContacts([]);
        setIsLoadingPreview(false);
      }
    });

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedPipeline, selectedStages]);

  const previewMatches = useMemo(() => {
    const cbEmails = new Set(cbContacts.map(c => c.email?.toLowerCase()).filter(Boolean));

    const seen = new Set();
    const uniqueContacts = previewContacts.filter(contact => {
      const id = contact.id || contact.properties?.email || '';
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    const syncedCount = uniqueContacts.filter(c => {
      const email = c.properties?.email || c.email;
      return cbEmails.has(email?.toLowerCase());
    }).length;

    return {
      count: uniqueContacts.length,
      syncedCount,
      samples: uniqueContacts.slice(0, 10).map((c, index) => {
        const props = c.properties || c;
        const email = props.email || 'No email';
        const stableId = c.id || email || `contact-${index}`;
        return {
          id: stableId,
          name: `${props.firstname || props.firstName || ''} ${props.lastname || props.lastName || ''}`.trim() || 'Unknown',
          email,
          isSynced: cbEmails.has(email?.toLowerCase()),
        };
      }),
    };
  }, [previewContacts, cbContacts]);

  const currentPipeline = pipelines.find(p => p.id === selectedPipeline);
  const currentStages = currentPipeline?.stages || [];

  function handleNext() {
    if (activeStep === 0) {
      if (!confirmPaying) {
        toast.error("Please confirm these are currently paying clients.");
        return;
      }
      if (!selectedPipeline) {
        toast.error("Please select a deal pipeline.");
        return;
      }
      if (selectedStages.length === 0) {
        toast.error("Please select at least one deal stage.");
        return;
      }
      if (isLoadingPreview) {
        toast.info("Please wait for contacts to finish loading...");
        return;
      }
      persistContactFilter();
    }

    if (activeStep === 1) {
      axios.post(`${API_URL}/hubspot/field-mappings`, filterHiddenFields(mappings), {
        headers: { 'Content-Type': 'application/json' }
      }).catch((error) => {
        console.error("Failed to save field mappings:", error.message);
      });
    }

    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  }

  function handleBack() {
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  function handleDryRun() {
    if (!selectedPipeline || selectedStages.length === 0) {
      toast.warning("Please configure pipeline/stage filter in step 1 first.");
      return;
    }

    if (isLoadingPreview) {
      toast.info("Please wait for contacts to load...");
      return;
    }

    if (previewContacts.length === 0) {
      toast.warning("No contacts found matching your filter. Please check your filter settings.");
      return;
    }

    const matched = previewContacts;
    const importCount = matched.length;

    const cbEmails = new Set(cbContacts.map(c => c.email?.toLowerCase()).filter(Boolean));
    const alreadySynced = matched.filter(c => {
      const email = c.properties?.email || c.email;
      return cbEmails.has(email?.toLowerCase());
    }).length;
    const newContacts = importCount - alreadySynced;

    const emails = matched.map(c => (c.properties?.email || c.email)?.toLowerCase()).filter(Boolean);
    const uniqueEmails = new Set(emails);
    const duplicates = emails.length - uniqueEmails.size;

    const skipped = matched.filter(c => {
      const props = c.properties || c;
      return !props.email || !props.firstname || !props.lastname;
    }).length;

    let conflicts = 0;
    const twoWayMappings = Object.values(mappings).filter(m => m.direction === 'Two-way');
    if (twoWayMappings.length > 0) {
      conflicts = Math.min(alreadySynced, Math.floor(alreadySynced * 0.2));
    } else {
      conflicts = Math.min(alreadySynced, Math.floor(alreadySynced * 0.1));
    }

    const result = {
      importCount,
      alreadySynced,
      newContacts,
      duplicates,
      skipped,
      conflicts,
      syncDirection: {
        twoWay: twoWayMappings.length,
        cbToHs: Object.values(mappings).filter(m => m.direction === 'CB→CRM').length,
        hsToCb: Object.values(mappings).filter(m => m.direction === 'CRM→CB').length
      }
    };

    setDryRunResult(result);
    toast.success(`Safe Import completed: ${importCount} contacts will be synced`);
  }

  const calculateSyncTimeout = (contactCount) => {
    const baseTimeout = 60000;
    const perContactTime = 3000;
    const calculatedTimeout = baseTimeout + (contactCount * perContactTime);
    return Math.min(calculatedTimeout, 600000);
  };

  const calculateEstimatedSyncTime = (contactCount) => {
    const baseTimeSeconds = 60;
    const perContactSeconds = 3;
    const estimatedSeconds = baseTimeSeconds + (contactCount * perContactSeconds);
    const finalSeconds = Math.min(estimatedSeconds, 600);
    const minutes = Math.floor(finalSeconds / 60);
    const seconds = finalSeconds % 60;
    if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ${seconds > 0 ? `${seconds} second${seconds > 1 ? 's' : ''}` : ''}`;
    }
    return `${seconds} second${seconds > 1 ? 's' : ''}`;
  };

  function handleStartSync() {
    if (!dryRunResult) {
      toast.warning("Please run dry run first.");
      return;
    }

    const contactCount = dryRunResult.importCount || 0;
    const estimatedTime = calculateEstimatedSyncTime(contactCount);

    if (contactCount >= 100) {
      const confirmed = window.confirm(
        `You are about to sync ${contactCount} contacts. This may take approximately ${estimatedTime}.\n\n` +
        `The sync will continue in the background even if you see a timeout message. Do you want to proceed?`
      );
      if (!confirmed) return;
    }

    if (stopIfOverLimit && dryRunResult.importCount > killSwitchLimit) {
      const confirmed = window.confirm(
        `Dry run indicates ${dryRunResult.importCount} would be imported which is above your kill-switch limit of ${killSwitchLimit}. ` +
        `This may take approximately ${estimatedTime}. Are you sure you want to proceed?`
      );
      if (!confirmed) return;
    }

    axios.post(`${API_URL}/hubspot/field-mappings`, filterHiddenFields(mappings), {
      headers: { 'Content-Type': 'application/json' }
    })
    .then(() => {
      persistContactFilter();
      setIsSyncing(true);
      const startTime = Date.now();
      setSyncStartTime(startTime);

      const dynamicTimeout = calculateSyncTimeout(contactCount);

      const syncPayload = {
        filter: { pipelineId: selectedPipeline, stageIds: selectedStages },
        killSwitchLimit,
        stopIfOverLimit,
        dryRunResult
      };

      const baseTimeSeconds = 60;
      const perContactSeconds = 3;
      const estimatedSeconds = Math.min(baseTimeSeconds + (contactCount * perContactSeconds), 600);

      syncIntervalRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        const remaining = Math.max(0, estimatedSeconds - elapsed);
        const mins = Math.floor(remaining / 60);
        const secs = Math.floor(remaining % 60);
        if (mins > 0) {
          setEstimatedTimeRemaining(`${mins}m ${secs}s`);
        } else if (remaining > 0) {
          setEstimatedTimeRemaining(`${secs}s`);
        } else {
          setEstimatedTimeRemaining('Completing...');
        }
      }, 1000);

      return axios.post(`${API_URL}/hubspot/sync`, syncPayload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: dynamicTimeout
      }).finally(() => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
        setEstimatedTimeRemaining(null);
      });
    })
    .then(async () => {
      setIsSyncing(false);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      setSyncStartTime(null);
      setEstimatedTimeRemaining(null);

      const alreadySyncedCount = dryRunResult?.alreadySynced || 0;
      const newContactsCount = dryRunResult?.newContacts || 0;
      const totalSynced = dryRunResult?.importCount || 0;

      await new Promise(resolve => setTimeout(resolve, 1000));

      const cbResponse = await axios.get(`${API_URL}/hubspot/cb-contacts`);
      const updatedCbContacts = cbResponse.data.clients || [];
      const updatedCbCount = updatedCbContacts.filter(c => !c.archived).length;

      setCbContacts(updatedCbContacts);
      setSyncStats({
        activeInCB: updatedCbCount,
        inCRMFilter: totalSynced,
        alreadySynced: alreadySyncedCount,
        newContacts: newContactsCount
      });
      setSyncCompleted(true);

      window.dispatchEvent(new CustomEvent('refreshHubspotStats'));

      if (alreadySyncedCount > 0 && newContactsCount === 0) {
        toast.success(`Sync completed: ${alreadySyncedCount} existing contact(s) updated`);
      } else if (newContactsCount > 0) {
        toast.success(`Sync completed: ${newContactsCount} new contact(s) added, ${alreadySyncedCount} updated`);
      } else {
        toast.success('Sync completed successfully!');
      }
    })
    .catch(async (error) => {
      console.error("Sync failed:", error.message);

      if (error.code === 'ECONNABORTED') {
        await new Promise(resolve => setTimeout(resolve, 3000));

        try {
          const statsResponse = await axios.get(`${API_URL}/hubspot/sync-stats`);
          const totalSyncedFromAPI = statsResponse.data?.totalSyncedContacts || 0;
          const expectedCount = dryRunResult?.importCount || 0;
          const syncSuccessful = totalSyncedFromAPI >= (expectedCount - 5) || totalSyncedFromAPI >= expectedCount * 0.95;

          if (syncSuccessful) {
            const cbResponse = await axios.get(`${API_URL}/hubspot/cb-contacts`);
            const updatedCbContacts = cbResponse.data.clients || [];
            setCbContacts(updatedCbContacts);

            setSyncStats({
              activeInCB: updatedCbContacts.filter(c => !c.archived).length,
              inCRMFilter: dryRunResult?.importCount || 0,
              alreadySynced: dryRunResult?.alreadySynced || 0,
              newContacts: dryRunResult?.newContacts || 0
            });
            setSyncCompleted(true);
            setIsSyncing(false);

            if (syncIntervalRef.current) {
              clearInterval(syncIntervalRef.current);
              syncIntervalRef.current = null;
            }
            setEstimatedTimeRemaining(null);
            setSyncStartTime(null);

            window.dispatchEvent(new CustomEvent('refreshHubspotStats'));
            toast.success(`Sync completed successfully!`);
            return;
          } else {
            toast.warning(`Sync may still be processing. ${totalSyncedFromAPI} of ${expectedCount} contacts synced so far.`);
          }
        } catch (checkError) {
          console.error("Error checking sync status:", checkError);
        }
      }

      setIsSyncing(false);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      setEstimatedTimeRemaining(null);
      setSyncStartTime(null);

      let errorMessage = "Sync failed: ";
      if (error.code === 'ECONNABORTED') {
        errorMessage += "Request timed out. Please check if contacts were synced and try again if needed.";
      } else if (error.response?.status === 401) {
        errorMessage += "Authentication failed. Please log in again.";
      } else if (error.response?.status === 429) {
        errorMessage += "Rate limit exceeded. Please try again later.";
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || "Unknown error occurred.";
      }

      toast.error(errorMessage);
    });
  }

  async function handleConfirmSync() {
    if (!syncCompleted) {
      toast.warning("Please complete the sync first.");
      return;
    }

    setIsConfirming(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      let retries = 3;
      let totalSyncedFromAPI = 0;
      const dryRunCount = syncStats?.inCRMFilter || 0;

      while (retries > 0) {
        try {
          const response = await axios.get(`${API_URL}/hubspot/sync-stats`);
          totalSyncedFromAPI = response.data?.totalSyncedContacts || 0;
          if (totalSyncedFromAPI > 0 || retries === 1) break;
          if (retries > 1) await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (err) {
          console.error("Failed to fetch sync stats:", err);
        }
        retries--;
      }

      const totalSynced = Math.max(totalSyncedFromAPI, dryRunCount);
      toast.success(`Sync finalized successfully! ${totalSynced} contact(s) synced.`);
      window.dispatchEvent(new CustomEvent('refreshHubspotStats'));

      await new Promise(resolve => setTimeout(resolve, 500));
      setIsConfirming(false);

      if (typeof onClose === 'function') {
        onClose('refreshStats');
      }
    } catch (error) {
      console.error("Failed to fetch sync stats:", error);
      toast.success(`Sync finalized! ${syncStats?.inCRMFilter || 0} contact(s) processed.`);
      window.dispatchEvent(new CustomEvent('refreshHubspotStats'));
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsConfirming(false);
      if (typeof onClose === 'function') {
        onClose('refreshStats');
      }
    }
  }

  function persistContactFilter() {
    const filterToSave = { pipelineId: selectedPipeline, stageIds: selectedStages, confirmPaying };
    setIsSavingConfig(true);
    axios.get(`${API_URL}/hubspot/status`)
    .then((response) => {
      const raw = response?.data?.data?.config;
      let configObj = {};
      try {
        configObj = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
      } catch (e) {
        configObj = {};
      }
      const merged = { ...(configObj || {}), contactFilter: filterToSave };
      return axios.post(`${API_URL}/hubspot/config`, merged);
    })
    .catch((error) => {
      console.error("Failed to save contact filter configuration:", error);
      toast.error("Failed to save filter settings. Please try again.");
    })
    .finally(() => setIsSavingConfig(false));
  }

  const getFieldType = (fieldKey) => {
    const field = DEFAULT_CLIENTBLOOM_FIELDS.find(f => f.key === fieldKey);
    return field?.type || 'text';
  };

  const getHSFieldType = (hsFieldKey) => {
    if (!hsFieldKey) return 'text';
    const key = (hsFieldKey || '').toLowerCase();
    if (key === 'email' || key.includes('email')) return 'email';
    if (key === 'phone' || key.includes('phone')) return 'phone';
    if (key.includes('date') || key.includes('time')) return 'date';
    if (key === 'hs_object_id' || key.endsWith('_id')) return 'id';
    return 'text';
  };

  const isCompatibleMapping = (cbFieldKey, hsFieldKey) => {
    if (!hsFieldKey) return true;
    const cbType = getFieldType(cbFieldKey);
    const hsType = getHSFieldType(hsFieldKey);
    if (cbType === 'email' && hsType !== 'email') return false;
    if (cbType === 'phone' && hsType !== 'phone') return false;
    if (cbType === 'id' && hsType !== 'id') return false;
    return true;
  };

  const filterHiddenFields = (mappingsToFilter) => {
    const filtered = { ...mappingsToFilter };
    HIDDEN_FIELDS.forEach(field => delete filtered[field]);
    return filtered;
  };

  function updateMapping(fieldKey, patch) {
    const currentMapping = mappings[fieldKey] || { crmField: "", direction: "Off", readOnly: false };

    if ((fieldKey === 'id' || fieldKey === 'email') && patch.crmField) return;

    if (fieldKey === 'id' && patch.direction === 'Two-way') {
      patch.direction = 'CRM→CB';
    }

    let newMapping = { ...currentMapping, ...patch };

    if (patch.crmField && patch.crmField !== '' && currentMapping.direction === "Off") {
      newMapping.direction = fieldKey === 'id' ? "CRM→CB" : "Two-way";
    }

    if (patch.crmField && patch.crmField !== '') {
      const isCompatible = isCompatibleMapping(fieldKey, patch.crmField);
      if (!isCompatible) {
        const cbField = DEFAULT_CLIENTBLOOM_FIELDS.find(f => f.key === fieldKey);
        const hsField = hsContactFields.find(f => f.key === patch.crmField);
        toast.warning(
          `Warning: Mapping ${cbField?.label} to ${hsField?.label} may cause data issues.`,
          { autoClose: 5000 }
        );
      }
    }

    const newMappings = { ...mappings, [fieldKey]: newMapping };
    setMappings(newMappings);

    axios.post(`${API_URL}/hubspot/field-mappings`, filterHiddenFields(newMappings), {
      headers: { 'Content-Type': 'application/json' }
    }).catch((error) => {
      console.error("Failed to save field mappings:", error.message);
    });
  }

  function renderStepContent(step) {
    if (step === 0) {
      return (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Choose which deal pipeline and stage(s) to pull clients from.
          </Typography>

          {isLoadingPipelines ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading pipelines from HubSpot...
              </Typography>
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="pipeline-select-label">Deal Pipeline</InputLabel>
                <Select
                  labelId="pipeline-select-label"
                  value={selectedPipeline}
                  label="Deal Pipeline"
                  onChange={(e) => {
                    setSelectedPipeline(e.target.value);
                    setSelectedStages([]);
                  }}
                >
                  {pipelines.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select the pipeline that contains your paying clients</FormHelperText>
              </FormControl>

              {selectedPipeline && currentStages.length > 0 && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="stage-select-label">Deal Stage(s)</InputLabel>
                  <Select
                    labelId="stage-select-label"
                    multiple
                    value={selectedStages}
                    label="Deal Stage(s)"
                    onChange={(e) => setSelectedStages(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((val) => {
                          const stage = currentStages.find(s => s.id === val);
                          return <Chip key={val} label={stage?.label || val} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {currentStages.map((stage) => (
                      <MenuItem key={stage.id} value={stage.id}>
                        <Checkbox checked={selectedStages.indexOf(stage.id) > -1} />
                        <ListItemText primary={stage.label} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select the stage(s) that indicate a contact is an active paying client</FormHelperText>
                </FormControl>
              )}
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Preview */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Live Preview
            </Typography>
            {isLoadingPreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading contacts...
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                  <Chip
                    label={`${previewMatches.count} contacts match`}
                    color={previewMatches.count > 0 ? "success" : "default"}
                    size="small"
                  />
                  <Chip
                    label={`${previewMatches.syncedCount} already in CB`}
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                </Box>
                {previewMatches.samples.length > 0 && (
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>In CB</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewMatches.samples.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>{s.name}</TableCell>
                          <TableCell>{s.email}</TableCell>
                          <TableCell>{s.isSynced ? "Yes" : "No"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {previewMatches.count > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Showing 10 of {previewMatches.count} matching contacts
                  </Typography>
                )}
              </>
            )}
          </Paper>

          <Box sx={{ mt: 2 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <Checkbox
                checked={confirmPaying}
                onChange={(e) => setConfirmPaying(e.target.checked)}
              />
              <Typography variant="body2">
                I confirm these are currently paying clients
              </Typography>
            </label>
          </Box>
        </Box>
      );
    }

    if (step === 1) {
      return (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Map ClientBloom fields to HubSpot properties
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>ClientBloom Field</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>HubSpot Property</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Direction</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DEFAULT_CLIENTBLOOM_FIELDS
                .filter(f => !HIDDEN_FIELDS.includes(f.key))
                .map((field) => {
                  const mapping = mappings[field.key] || { crmField: "", direction: "Off", readOnly: false };
                  const isReadOnly = mapping.readOnly;
                  const isIdField = field.key === 'id' || field.key === 'email';

                  return (
                    <TableRow key={field.key}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {field.label}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isIdField ? (
                          <Typography variant="body2" color="text.secondary">
                            {mapping.crmField}
                          </Typography>
                        ) : (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={mapping.crmField || ""}
                              onChange={(e) => updateMapping(field.key, { crmField: e.target.value })}
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Not mapped</em>
                              </MenuItem>
                              {hsContactFields.map((hsField) => (
                                <MenuItem key={hsField.key} value={hsField.key}>
                                  {hsField.label} ({hsField.key})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        {isReadOnly ? (
                          <Chip label={mapping.direction} size="small" variant="outlined" />
                        ) : (
                          <FormControl size="small">
                            <Select
                              value={mapping.direction || "Off"}
                              onChange={(e) => updateMapping(field.key, { direction: e.target.value })}
                            >
                              <MenuItem value="Two-way">Two-way</MenuItem>
                              <MenuItem value="CB→CRM">CB → HubSpot</MenuItem>
                              <MenuItem value="CRM→CB">HubSpot → CB</MenuItem>
                              <MenuItem value="Off">Off</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Box>
      );
    }

    if (step === 2) {
      return (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Safe Import — preview and sync
          </Typography>

          {/* Kill-switch */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                checked={stopIfOverLimit}
                onChange={(e) => setStopIfOverLimit(e.target.checked)}
              />
              <Typography variant="body2">
                Kill switch: stop if more than
              </Typography>
              <input
                type="number"
                value={killSwitchLimit}
                onChange={(e) => setKillSwitchLimit(Math.max(1, Math.min(10000, parseInt(e.target.value) || 500)))}
                style={{ width: 80, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
              />
              <Typography variant="body2">
                contacts would be imported
              </Typography>
            </Box>
          </Paper>

          {/* Dry Run Button */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleDryRun}
              sx={{
                backgroundColor: '#572AF1',
                '&:hover': { backgroundColor: '#471de0' },
                textTransform: 'none'
              }}
            >
              Run Dry Run
            </Button>
          </Box>

          {/* Dry Run Results */}
          {dryRunResult && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Dry Run Results
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="body2">Total contacts to sync:</Typography>
                <Typography variant="body2" fontWeight={600}>{dryRunResult.importCount}</Typography>

                <Typography variant="body2">Already in ClientBloom:</Typography>
                <Typography variant="body2" fontWeight={600}>{dryRunResult.alreadySynced}</Typography>

                <Typography variant="body2">New contacts to create:</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">{dryRunResult.newContacts}</Typography>

                <Typography variant="body2">Duplicates found:</Typography>
                <Typography variant="body2" fontWeight={600}>{dryRunResult.duplicates}</Typography>

                <Typography variant="body2">Incomplete (missing fields):</Typography>
                <Typography variant="body2" fontWeight={600} color={dryRunResult.skipped > 0 ? "warning.main" : "inherit"}>{dryRunResult.skipped}</Typography>
              </Box>

              {stopIfOverLimit && dryRunResult.importCount > killSwitchLimit && (
                <Box sx={{ mt: 2, p: 1, backgroundColor: '#fff3cd', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningAmberOutlinedIcon color="warning" fontSize="small" />
                  <Typography variant="body2" color="warning.dark">
                    Contact count ({dryRunResult.importCount}) exceeds kill-switch limit ({killSwitchLimit})
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Sync Button */}
          {dryRunResult && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleStartSync}
                disabled={isSyncing}
                sx={{
                  backgroundColor: '#32bca3',
                  '&:hover': { backgroundColor: '#2aa892' },
                  textTransform: 'none',
                  mr: 2
                }}
              >
                {isSyncing ? 'Syncing...' : 'Start Sync'}
              </Button>
              {isSyncing && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  {estimatedTimeRemaining && (
                    <Typography variant="body2" color="text.secondary">
                      Est. remaining: {estimatedTimeRemaining}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Sync Complete */}
          {syncCompleted && syncStats && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f0fff4' }}>
              <Typography variant="subtitle2" gutterBottom color="success.main">
                Sync Completed Successfully
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="body2">Active clients in CB:</Typography>
                <Typography variant="body2" fontWeight={600}>{syncStats.activeInCB}</Typography>

                <Typography variant="body2">Contacts in CRM filter:</Typography>
                <Typography variant="body2" fontWeight={600}>{syncStats.inCRMFilter}</Typography>

                <Typography variant="body2">Already synced:</Typography>
                <Typography variant="body2" fontWeight={600}>{syncStats.alreadySynced}</Typography>

                <Typography variant="body2">New contacts added:</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">{syncStats.newContacts}</Typography>
              </Box>
            </Paper>
          )}
        </Box>
      );
    }

    return null;
  }

  return (
    <Dialog
      open
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      className="hubspot-contact-sync-dialog"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src="/hubspot.png" alt="HubSpot" style={{ height: 28 }} />
          <Typography variant="h6">HubSpot Contact Sync</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && (
          <Button onClick={handleBack} sx={{ textTransform: 'none' }}>
            Back
          </Button>
        )}
        <Button onClick={() => onClose()} color="error" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isSavingConfig}
            sx={{
              backgroundColor: '#572AF1',
              '&:hover': { backgroundColor: '#471de0' },
              textTransform: 'none'
            }}
          >
            {isSavingConfig ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Next'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirmSync}
            disabled={!syncCompleted || isConfirming}
            sx={{
              backgroundColor: '#32bca3',
              '&:hover': { backgroundColor: '#2aa892' },
              textTransform: 'none'
            }}
          >
            {isConfirming ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirm & Close'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
